import React from 'react';
import { graphql } from 'gatsby';
import get from 'lodash/get';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { getImage } from 'gatsby-plugin-image';
import readingTime from 'reading-time';
import {
  Text, ArticleTeaser, Pagination, HeroBanner
} from '@comicrelief/component-library';

import Layout from '../../components/Layout/Layout';
import { SEO, SEO_PAGE_TYPES } from '../../components/SEO/SEO';
import NewsFilter from '../../components/NewsFilter/NewsFilter';
import newsDefaultImage from '../../../static/assets/news-default.jpg';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';

import {
  Wrapper, Container, Section, Grid, Items
} from './Articles.styles';

const handleSelect = () => {
  window.scrollTo({ top: 100, behavior: 'smooth' });
};

const handlePaginationUrl = (page) => {
  if (page === 1) {
    return '/news/';
  }
  return `/news/page-${page}`;
};

const Articles = (props) => {
  const articles = get(props, 'data.allContentfulPageArticle.edges');
  const pageInfo = get(props, 'data.allContentfulPageArticle.pageInfo');

  // To match the new IA (Information Architecture) from Numiko:
  const parentPages = [{ title: 'About Us', path: 'about' }];
  const thisPageTitle = 'News';

  return (
    <Layout>
      <Wrapper>

        <Breadcrumb
          thisPageTitle={thisPageTitle}
          parentPages={parentPages}
        />

        <HeroBanner
          pageBackgroundColour="grey_light"
          textBannerCopyBackgroundColour="blue"
          variant="text_banner"
          copyColour="white"
          paddingTop="0rem"
          paddingBottom="0rem"
        >
          <Text tag="h1">
            {thisPageTitle}
          </Text>
        </HeroBanner>

        <Container>
          <Section>
            <NewsFilter selectedCategory="All" />
            <Grid>
              {articles.map(({ node }) => {
                const {
                  image, path, displayDate, title, id, body
                } = node;

                const thisImageData = getImage(image);
                const thisImageFallback = thisImageData?.images?.fallback?.src ?? newsDefaultImage;
                const thisImageSrcSet = thisImageData?.images?.sources[0]?.srcSet
                ?? newsDefaultImage;
                const thisImageLow = thisImageData?.placeholder?.fallback ?? newsDefaultImage;
                const thisImageDesc = image?.description ?? '';

                const URL = `/news/${path}`;
                const content = body && documentToPlainTextString(body.raw);
                const time = content && readingTime(content);
                return (
                  <Items key={id}>
                    <ArticleTeaser
                      href={URL}
                      image={thisImageFallback}
                      images={thisImageSrcSet}
                      imageLow={thisImageLow}
                      date={displayDate}
                      title={title}
                      alt={thisImageDesc}
                      time={time && time.text}
                    />
                  </Items>
                );
              })}
            </Grid>
          </Section>
          <div data-test="news-pagination">
            <Pagination
              disabledBackgroundColor="grey_dark"
              disabledColor="grey_light"
              maxPages={10}
              totalPages={pageInfo.pageCount}
              currentPage={pageInfo.currentPage}
              createURL={value => handlePaginationUrl(value)}
              onSelect={handleSelect}
              showFirst
              showPrevious={false}
              showNext={false}
              showLast
            />
          </div>
        </Container>
      </Wrapper>
    </Layout>
  );
};

export default Articles;

export const Head = props => (
  <SEO
    title="News"
    allData={props}
    pageType={SEO_PAGE_TYPES.ARTICLES}
  >
    {(siteUrl, linkedDataObj) => (
      <script type="application/ld+json" key="carousel">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'ItemList',
          itemListElement: linkedDataObj?.theseArticleEdges.map((article, position) => ({
            '@type': 'ListItem',
            position,
            url: `${siteUrl}/news/${article.node.path}`
          }))
        })}
      </script>
    )}
  </SEO>
);

export const pageQuery = graphql`
  query getArticles($skip: Int, $limit: Int) {
    allContentfulPageArticle(
      skip: $skip
      limit: $limit
      filter: {type: {eq: "News Article"}}
      sort: {displayDate: DESC}
    ) {
      totalCount
      pageInfo {
        perPage
        currentPage
        hasPreviousPage
        hasNextPage
        itemCount
        pageCount
      }
      edges {
        node {
          title
          path
          type
          id
          body {
            raw
          }
          displayDate(formatString: "Do MMMM YYYY")
          image {
            description
            ...Picture
          }
        }
      }
    }
    allContentfulPageLandingPage(filter: {path: {eq: "news-seo"}}) {
      edges {
        node {
          path
          title
          imageSeo {
            ...Picture
          }
          summary {
            summary
          }
        }
      }
    }
  }
`;
